import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "../../../../components/Button/Button";
import { RoutesRouter } from "../../../../models/Routes";
import { useNavigate } from "react-router-dom";
import { getI18n, useTranslation } from "react-i18next";
import {
  downloadResponseAsFile,
  useComponentVisibles,
} from "../../../../utils/functions";
import { useToastAlert } from "../../../../components/ToastAlert/ToastAlert";
import { DURATION_TOAST } from "../../../../utils/costants";
import Chevron from "../../../../components/Forms/FormInput/FormSelect/Chevron/Chevron";
import getClasses from "../../../../utils/getClasses";
import { QuestionColumn } from "../../QuestionsColumn/QuestionsColumn";
import {
  COLUMN_NAMES,
  IQuestionsButtonOptions,
  QuestionType,
} from "../../Template/Template";
import { Question } from "../../QuestionsColumn/Question/Questions";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { KeyStorage, getStorageItem } from "../../../../utils/localStorage";
import EditPencil from "../../../../assets/img/edit-pencil.svg";
import ClosedEyeSvg from "../../../../assets/img/closed-eye.svg";
import EyeSvg from "../../../../assets/img/eye.svg";
import EmptyTable from "../../../../components/EmptyTable/EmptyTable";
import { CORE, Category, IQuestion } from "../../../../models/SurveyModels";
import { useEndpoints } from "../../../../hooks/useEndpoints";
import Pagination from "../../../../components/Pagination/Pagination";
import { optionsPage } from "../../../Companies/Companies";
import Skeleton from "react-loading-skeleton";

require("./QuestionPage.scss");

export const QuestionsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<QuestionType[]>();

  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const handleRefAssignment =
    (index: number) => (ref: HTMLDivElement | null) => {
      refs.current[index] = ref;
    };
  const { isComponentVisible, setComponentVisible } = useComponentVisibles(
    false,
    refs
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [filter, setFilter] = useState<"true" | "false">("true");
  const [filterCategory, setFilterCategory] = useState<CORE>();
  const [filterSubCategory, setFilterSUbCategory] = useState<Category>();
  const [arrayFilterSubCategory, setArrayFilterSUbCategory] = useState<
    Category[]
  >([]);
  const [inputValue, setInputValue] = React.useState("");

  const [myLimit, setMyLimit] = useState<number>(
    Number(Object.values(optionsPage[1])[0])
  );

  const [skip, setSkip] = useState<number>(0);
  const [count, setCount] = useState<number>(0);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const { getCategories, getAllQuestions, patchQuestion, exportQuestions } =
    useEndpoints();
  const { showToast } = useToastAlert();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const response = await exportQuestions({
        enabled: filter,
        category: filterSubCategory?.id,
        coreArea: filterCategory,
        text: inputValue,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await downloadResponseAsFile(response, "domande.xlsx");
    } catch (err) {
      console.error("Export questions failed:", err);
      showToast({
        message: `${t("home.survey.exportError")}`,
        type: "error",
        duration: DURATION_TOAST,
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getAllQuestions({
      enabled: filter,
      category: filterSubCategory?.id,
      coreArea: filterCategory,
      text: inputValue,
      skip: skip,
      limit: myLimit,
    }).then(({ items, count }) => {
      const questionsFordrag = items?.map((question: IQuestion) => {
        return { ...question, column: COLUMN_NAMES.QUESTION };
      });
      setQuestions(questionsFordrag);
      setCount(count);
      setIsLoading(false);
    });
  }, [
    filter,
    filterCategory,
    filterSubCategory?.id,
    getAllQuestions,
    inputValue,
    myLimit,
    skip,
  ]);

  useEffect(() => {
    getCategories({
      language: getI18n().language.includes("it") ? "ITALIAN" : "ENGLISH",
      coreArea: filterCategory,
    }).then((resp) => {
      setIsLoadingCategories(true);
      setArrayFilterSUbCategory(resp);
      !resp.some(({ name }) => name === filterSubCategory?.name) &&
        setFilterSUbCategory(undefined);
      setIsLoadingCategories(false);
    });
  }, [filterCategory, filterSubCategory, getCategories, t]);

  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => IQuestionsButtonOptions
    ): Array<IQuestionsButtonOptions> => {
      if (!questions) return [] as Array<IQuestionsButtonOptions>;
      return questions.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [questions]
  );

  const changeKeyById = (id: string) => {
    setQuestions((prevQuestions) => {
      return prevQuestions?.map((question) => {
        if (question.id === id) {
          return {
            ...question,
            enabled: !question.enabled,
          };
        }
        return question;
      });
    });
  };

  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: any) => {
        let buttons: IQuestionsButtonOptions = [];
        buttons.push(
          {
            onClick: (item: QuestionType) => {
              navigate(`${RoutesRouter.questions}/${item.id}/edit`);
            },
            label: `${t("home.edit")}`,
            colorTheme: "blue",
            icon: <img src={EditPencil} alt="edit" />,
          },
          {
            onClick: (item: QuestionType) => {
              patchQuestion(item.id).then((resp) => {
                if (resp.status === 200) {
                  changeKeyById(item.id);
                }
              });
            },
            label: `${t(
              `home.survey.actions.${resource.enabled ? "disable" : "enable"}`
            )}`,
            colorTheme: "blue",
            icon: (
              <img src={resource.enabled ? ClosedEyeSvg : EyeSvg} alt="go" />
            ),
          }
        );
        return buttons;
      }),
    [generateButtons, navigate, patchQuestion, t]
  );

  const filtersLine = () => (
    <div className="container-filters">
      <div className="resources-searching container__top my-3">
        <div className="form-group">
          <input
            id="searchResource"
            type="text"
            placeholder={t("home.questions.inputPlaceholder") as string}
            value={inputValue}
            onChange={handleInputChange}
            className="form-control filter-questions"
          />
          <i className="icon-search"></i>
          {inputValue && (
            <i
              className="icon-close"
              onClick={() => {
                setInputValue("");
              }}
            ></i>
          )}
        </div>
      </div>
      <div className="container-filters">
        <div className="resources-searching row container__top my-3">
          <div
            ref={handleRefAssignment(1)}
            className="filter-container col-lg-12 mt-3 col-xl-3"
          >
            <div
              className="filter-wrapper col-lg-6 col-xl-12"
              onClick={() => {
                setComponentVisible(1);
              }}
            >
              <span className="language-menu capitalize-first-letter">
                {t("home.questions.filterActive")}:{" "}
                {t(`home.questions.filters.${filter}`)}
              </span>

              <Chevron direction={isComponentVisible[1] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[1] && (
              <div className="switch-filters">
                {(["true", "false"] as const).map((item) => (
                  <div
                    className={`filters-item ${getClasses({
                      active: item === filter,
                    })}`}
                    key={item}
                  >
                    <div
                      onClick={() => {
                        setFilter(item);
                        setComponentVisible(1);
                      }}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <span>{t(`home.companies.filters.${item}`)}</span>
                      {item === filter && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            ref={handleRefAssignment(2)}
            className="filter-container col-lg-12 mt-3 col-xl-3"
          >
            <div
              className="filter-wrapper col-lg-6 col-xl-12"
              onClick={() => {
                setComponentVisible(2);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {t("home.questions.filterActive")}:{" "}
                {t(
                  `home.questions.filters.${
                    filterCategory ? filterCategory : "all"
                  }`
                )}
                {filterCategory && (
                  <i
                    className="icon-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterCategory(undefined);
                    }}
                  ></i>
                )}
              </span>
              <Chevron direction={isComponentVisible[2] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[2] && (
              <div className="switch-filters">
                {Object.entries(CORE)
                  .map(([key, value]) => ({ key, value }))
                  .map((item, index) => (
                    <div
                      className={`filters-item ${getClasses({
                        active: item.value === filterCategory,
                      })}`}
                      key={index}
                    >
                      <div
                        onClick={() => {
                          setFilterCategory(item.value);
                          setComponentVisible(2);
                        }}
                        className="menu-item d-flex justify-content-between gap-1"
                      >
                        <span>{t(`home.questions.filters.${item.value}`)}</span>
                        {item.value === filterCategory && (
                          <i className="icon icon-checkmark icon-square-checked me-1"></i>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div
            ref={handleRefAssignment(3)}
            className="filter-container col-lg-12 mt-3 col-xl-4"
          >
            <div
              className="filter-wrapper col-lg-6 col-xl-12"
              onClick={() => {
                setComponentVisible(3);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {t("home.questions.filterActive")}:{" "}
                <>{filterSubCategory?.name || t(`selectOptions.ALL`)}</>
                {filterSubCategory?.name && (
                  <i
                    className="icon-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterSUbCategory(undefined);
                    }}
                  ></i>
                )}
              </span>

              <Chevron direction={isComponentVisible[3] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[3] && (
              <div className="switch-filters col-lg-4 mt-3 col-xl-3">
                {!isLoadingCategories ? (
                  arrayFilterSubCategory.map((item, index) => (
                    <div
                      className={`filters-item ${getClasses({
                        active: item.id === filterSubCategory?.id,
                      })}`}
                      key={index}
                    >
                      <div
                        onClick={() => {
                          setFilterSUbCategory(item);
                          setComponentVisible(3);
                        }}
                        className="menu-item d-flex justify-content-between gap-1"
                      >
                        <div>
                          <i
                            style={{
                              color: item.color,
                            }}
                            className={`icon ${item.icon}`}
                          ></i>
                          <span>{item.name}</span>
                        </div>
                        {item.id === filterSubCategory?.id && (
                          <i className="icon icon-checkmark icon-square-checked me-1"></i>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <Skeleton count={5} height={30} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div></div>
    </div>
  );

  const returnItemsForColumn = useMemo(() => {
    if (filter) {
      const filteredQuestions = questions?.filter(
        (question) => String(question.enabled) === filter
      );
      return filteredQuestions?.map((item, index) => (
        <Question
          {...item}
          noDrag
          key={item.id}
          setItems={setQuestions}
          index={index}
          buttonsOption={buttonsOption}
        />
      ));
    } else
      return questions?.map((item, index) => (
        <Question
          {...item}
          noDrag
          key={item.id}
          setItems={setQuestions}
          index={index}
          buttonsOption={buttonsOption}
        />
      ));
  }, [buttonsOption, filter, questions]);
  return (
    <div className="container-custom questions-container-page">
      <div className="container__top">
        <div>
          <h1 className="title-page"> {t("home.survey.questions.title")}</h1>
          <p>{t("home.survey.description")}</p>
        </div>
        <div className=" my-3 d-flex gap-2">
          <Button outline onClick={handleExport} disabled={isExporting}>
            {isExporting
              ? t("home.survey.exporting")
              : t("home.survey.exportExcel")}
          </Button>
          <Button outline onClick={() => navigate(RoutesRouter.addQuestion)}>
            {t("home.questions.questionButton")}{" "}
            <span className="plus-char">+</span>
          </Button>
        </div>
      </div>
      {filtersLine()}
      <div>
        {!isLoading && questions && questions?.length > 0 && (
          <DndProvider backend={HTML5Backend}>
            <QuestionColumn
              isLoading={false}
              lenghtItems={questions.length}
              className="questions-page-column"
              title="Questions"
            >
              {returnItemsForColumn}
            </QuestionColumn>
          </DndProvider>
        )}
        {!isLoading && questions?.length === 0 && (
          <EmptyTable
            className="question-page"
            title={`${t(`home.survey.questions.emptyTable.${filter}`)}`}
            description={t("home.companies.emptyTable.description")}
          />
        )}
        {isLoading && (
          <div className="wrapper-questions-column questions-page-column ">
            <Skeleton style={{ marginBottom: "10px" }} count={5} height={112} />
          </div>
        )}
      </div>
      {!isLoading && questions && questions?.length > 0 && (
        <Pagination
          limit={myLimit}
          count={count}
          skip={skip}
          setLimit={setMyLimit}
          setSkip={setSkip}
          options={optionsPage}
        />
      )}
    </div>
  );
};
